<!-- The drawing set, on screen before it is on paper. THIS FILE IS YOURS.

     A drawing you cannot see before you send it is a drawing you send wrong. The sheets are
     the same primitives the PDF and the DXF are made of, rendered as SVG at the paper's own
     size, so what is on the screen is what comes out of the printer -- including the scale
     bar, which is the only thing on a printed drawing that survives being photocopied. -->
<template>
  <div class="scrim" @click.self="$emit('close')">
    <section class="card" role="dialog" aria-modal="true" aria-labelledby="dwg-title">
      <header>
        <h1 id="dwg-title">Drawings</h1>
        <button class="x" type="button" aria-label="Back to the kitchen" @click="$emit('close')">
          <IonIcon :icon="closeOutline" />
        </button>
      </header>

      <nav class="pages" aria-label="Sheet">
        <button v-for="(s, i) in set" :key="s.id" type="button"
                :class="{ on: i === at }" @click="at = i">{{ s.title }}</button>
      </nav>

      <div class="paper" role="group" aria-label="Paper size">
        <span>Paper</span>
        <button type="button" :class="{ on: size === 'a3' }" @click="size = 'a3'">A3</button>
        <button type="button" :class="{ on: size === 'a4' }" @click="size = 'a4'">A4</button>
      </div>

      <div class="sheet" v-html="svg" />

      <p class="fine">
        {{ current.title }} · {{ current.scale > 1 ? `1:${current.scale}` : 'no scale' }} ·
        sheet {{ at + 1 }} of {{ set.length }}, {{ size.toUpperCase() }} landscape.
      </p>

      <div class="row">
        <button class="go" type="button" @click="pdf" :disabled="busy">
          {{ busy === 'pdf' ? 'Making it…' : 'All sheets as PDF' }}
        </button>
        <button class="also" type="button" @click="dxf" :disabled="busy">
          {{ busy === 'dxf' ? 'Writing…' : 'DXF for CAD' }}
        </button>
      </div>
      <p class="fine last">
        The PDF is vectors, so it prints at true scale — check it against a ruler before you
        build from it. The DXF opens in any CAD or CAM package, in millimetres, every sheet
        laid out side by side.
      </p>
    </section>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { IonIcon } from '@ionic/vue';
import { closeOutline } from 'ionicons/icons';
import { sheets, toSvg } from '../kitchen/drawing.js';
import { toDxf } from '../kitchen/dxf.js';
import { buildDrawings } from '../kitchen/report.js';
import { shareFile } from '../kitchen/share.js';

const props = defineProps({ plan: Object, maker: Object });
const emit = defineEmits(['close', 'said']);

/*
 * A3 by default, on a phone as much as on a desk.
 *
 * The paper size used to follow the screen, which meant a phone got A4 -- and A4 forces the
 * plan down a scale step, so a kitchen that draws at 1:50 came out at 1:100, half the size
 * in each direction for no reason but the device it was previewed on. The sheet is a thing
 * that gets printed and sent, not a thing that gets looked at here, so it is A3 unless
 * somebody says otherwise. A3 and A4 are the same shape, so the preview is the same size
 * either way; what changes is how much of the page the kitchen fills.
 */
const size = ref('a3');
const set = computed(() => sheets(props.plan, props.maker, { size: size.value }));
const at = ref(0);
const current = computed(() => set.value[Math.min(at.value, set.value.length - 1)]);
const svg = computed(() => toSvg(current.value));
const busy = ref(null);

const stamp = () => new Date().toISOString().slice(0, 10);
const slug = () => (props.plan.name ? props.plan.name.replace(/[^\w]+/g, '-').toLowerCase() : 'kitchen');

async function pdf() {
  busy.value = 'pdf';
  try {
    const blob = await buildDrawings(set.value);
    const r = await shareFile(blob, `${slug()}-drawings-${stamp()}.pdf`,
      { title: 'Kitchen drawings', text: `Drawings from ${props.maker.name}` });
    emit('said', { shared: 'Sent.', downloaded: 'The drawings are in your downloads.' }[r] ?? null);
  } catch (e) {
    emit('said', `Could not make the drawings: ${e?.message ?? e}`);
  } finally { busy.value = null; }
}

async function dxf() {
  busy.value = 'dxf';
  try {
    const blob = new Blob([toDxf(set.value)], { type: 'image/vnd.dxf' });
    const r = await shareFile(blob, `${slug()}-${stamp()}.dxf`, { title: 'Kitchen drawings (DXF)' });
    emit('said', { shared: 'Sent.', downloaded: 'The DXF is in your downloads.' }[r] ?? null);
  } catch (e) {
    emit('said', `Could not write the DXF: ${e?.message ?? e}`);
  } finally { busy.value = null; }
}
</script>

<style scoped>
.scrim {
  position: fixed; inset: 0; z-index: 60;
  background: color-mix(in oklab, var(--k-ink) 46%, transparent);
  display: flex; align-items: flex-end; justify-content: center;
}
@media (min-width: 700px) { .scrim { align-items: center; padding: 20px; } }
.card {
  width: 100%; max-width: 900px; max-height: 94vh; overflow-y: auto;
  background: var(--k-paper); border-radius: 20px 20px 0 0;
  padding: 6px 16px calc(18px + env(safe-area-inset-bottom));
}
@media (min-width: 700px) { .card { border-radius: 16px; padding: 6px 20px 20px; } }
header { display: flex; align-items: center; gap: 10px; padding: 12px 0 8px; }
h1 { flex: 1; margin: 0; font: 600 20px/1.25 var(--ion-font-family, system-ui); color: var(--k-ink); letter-spacing: -.015em; }
.x { border: 0; background: none; color: var(--k-mute); cursor: pointer; width: 40px; height: 40px; display: grid; place-items: center; font-size: 20px; }

/* They WRAP. A five-sheet set in a sideways-scrolling row means the services sheet and the
   schedule are off the right-hand edge with nothing to say they are there -- and the row
   sits in a modal, so the swipe that would reach them fights the scroll of the modal
   itself. Every sheet is a button on screen. */
.pages { display: flex; flex-wrap: wrap; gap: 6px; padding: 0 0 10px; }
.pages button {
  flex: 0 1 auto; border: 0; border-radius: 8px; cursor: pointer; padding: 9px 12px; min-height: 38px;
  background: var(--k-fill); color: var(--k-dim);
  font: 500 12px/1 var(--ion-font-family, system-ui);
}
.pages button.on { background: var(--k-ink); color: var(--k-paper); }

.paper { display: flex; align-items: center; gap: 6px; padding: 0 0 10px; }
.paper span {
  font: 500 11px/1 var(--ion-font-family, system-ui); letter-spacing: .05em;
  text-transform: uppercase; color: var(--k-mute); margin-right: 2px;
}
.paper button {
  border: 1px solid var(--k-line); border-radius: 8px; cursor: pointer;
  padding: 6px 12px; min-height: 32px; background: var(--k-paper); color: var(--k-dim);
  font: 500 12px/1 var(--ion-font-family, system-ui);
}
.paper button.on { background: var(--k-ink); border-color: var(--k-ink); color: var(--k-paper); }

/* The sheet itself, on a grey ground so the paper reads as paper. */
.sheet {
  background: #8d9aa8; border-radius: 10px; padding: 10px;
  display: grid; place-items: center;
}
.sheet :deep(svg) {
  display: block; width: 100%; height: auto; max-height: 64vh;
  background: #fff; box-shadow: 0 2px 10px rgba(0,0,0,.25);
}

.fine { margin: 9px 0 0; font: 400 11.5px/1.45 var(--ion-font-family, system-ui); color: var(--k-mute); }
.fine.last { margin-top: 8px; }
.row { display: flex; align-items: center; gap: 10px; margin-top: 12px; }
.go {
  flex: 1; border: 0; border-radius: 11px; cursor: pointer;
  background: var(--k-ink); color: var(--k-paper);
  font: 600 14px/1 var(--ion-font-family, system-ui);
  padding: 14px; min-height: 50px;
}
.go:disabled, .also:disabled { opacity: .55; }
.also {
  border: 1px solid var(--k-line); border-radius: 11px; cursor: pointer;
  background: var(--k-paper); color: var(--k-dim);
  font: 500 13.5px/1 var(--ion-font-family, system-ui);
  padding: 14px 16px; min-height: 50px;
}
</style>
